<template>
  <div
    class="viewer"
    :class="[
      { 'viewer--interaction-enabled': state.interactionEnabled },
      theme ? 'viewer--theme-' + theme : null
    ]"
  >
    <div class="viewer__content">
      <canvas
        id="canvas-renderer"
        ref="canvas"
        class="viewer__render"
        :class="{ 'viewer__render--debug': state.debug }"
        :style="{ 'opacity': state.renderReady }"
      />

      <Transition name="fade">
        <div
          v-show="state.interactionEnabled"
          class="viewer__mobile-ui"
        >
          <div :class="'flip-coin collections-' + (theme || 'liberty')+'-common-flip-coin'" />
        </div>
      </Transition>

      <div
        v-if="debugUI"
        class="viewer__infos"
      >
        {{ frameID }}<br>{{ orientation }}
      </div>
    </div>
    <slot class="slot" />
  </div>
</template>

<script>
import { isTouchDevice } from '@/components/utils/touchDevice';
import DropAsset from './DropAsset';
// import * as Rendering from './renderer/renderer-three';
import * as Rendering from './renderer/renderer-babylon';

const CAM_OFFSET_MOUSE_COEFF = 0.3;
const CAM_OFFSET_GYRO_COEFF = 0.4;
const CAM_OFFSET_GYRO_HALF_ANGLE = 25;
const DEFAULT_PLAY_TIME = 1800;

// let Rendering = null;
let timeoutForDebug;
let timeoutPlay;

export default {
  props: {
    material: { type: String, default: null },
    normals: { type: String, default: null },
    theme: { type: String, default: null },
  },

  data() {
    return {
      state: {
        debug: false,
        playing: false,
        renderReady: false,
        interactionEnabled: false,
        touchDevice: false,
        init: false,
      },
      debugUI: false,
      frameID: 0,
      orientation: '',
      clickForDebuggingCount: 0,
      renderStopauto: true, // Stops the rendering after some time of inactivity
    };
  },

  computed: {
    domReady() {
      return this.$store.state.loaded;
    },
  },

  watch: {
    /* eslint-disable-next-line func-names */
    'state.playing': function (value) {
      if (value === true) {
        Rendering.startRendering();
      } else {
        Rendering.stopRendering();
      }
    },

    /* eslint-disable-next-line func-names */
    'state.debug': function (value) {
      Rendering.setDebugMode(value);
      if (value) this.play();
    },

    /* eslint-disable-next-line func-names */
    'state.touchDevice': function (value) {
      Rendering.enableOscillation(value);
    },

    /* eslint-disable-next-line func-names */
    domReady(value) {
      if (value === true
        && this.$data.state.init === false) {
        this.$nextTick(() => { this.init(); });
      }
    },

  },

  mounted() {
    if (!window.___differ___loading === 'true' || this.domReady === true) {
      this.$nextTick(() => { this.init(); });
    }

    /*
    import('./renderer/renderer-babylon').then((module) => {
      Rendering = module;
      this.init();
    });
    */
  },

  beforeDestroy() {
    clearTimeout(timeoutForDebug);
    clearTimeout(timeoutPlay);
    Rendering.stopRendering();
    this.$el.removeEventListener('mouseup', this.upHandler);
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('mousemove', this.moveHandler);
    window.removeEventListener('devicemotion', this.gyroHandler);
    window.removeEventListener('deviceorientation', this.gyroHandler);
    Rendering.dispose();
  },

  methods: {
    init() {
      const onReady = () => { this.onReady(); };
      const onCoinLoaded = () => { this.onCoinLoaded(); };
      const onIntroComplete = () => { this.onIntroComplete(); };
      const onRendered = () => { this.onRendered(); };
      const skipFrame = (window && window.___skip___frame === 'true');
      Rendering.init(null, {
        onReady,
        onCoinLoaded,
        onIntroComplete,
        onRendered,
        staticPath: this.$store.state.staticAssetsPath,
        skipFrame,
      }, this.$refs.canvas);

      Rendering.addCoin(this.$props.material, this.$props.normals);

      window.addEventListener('deviceorientation', this.gyroHandler, true);
      window.addEventListener('devicemotion', this.gyroHandler, true);

      this.$data.renderStopauto = (window && window.___render___stopauto === 'true');
      this.$data.state.init = true;

      if (process.env.VUE_APP_BUILD_MODE === 'package') return;

      /*
       * this part will be ignored on packaged version
       ************************************************************************
      */

      if (this.$data.debugUI) {
        Rendering.addDummy();
      }

      /* eslint-disable-next-line no-unused-vars */
      const dropArea = new DropAsset(this.$el, async ([file]) => {
        const ext = file.name.match(/\..{3,4}$/g)[0];

        switch (ext.toLowerCase()) {
          case '.hdr':
            Rendering.setEnvironment(URL.createObjectURL(file));
            break;
          case '.png':
          case '.jpg':
          case '.jpeg':
            Rendering.setNormalMap(URL.createObjectURL(file));
            break;
          case '.json':
            Rendering.setMaterialFromJSON(JSON.parse(await file.text()));
            break;
          default:
        }

        if (!this.$data.state.playing) setTimeout(() => { Rendering.render(); }, 500);

        /*
        * end of ignored part
        ************************************************************************
        */
      });
    },

    clickForDebugging() {
      if (process.env.VUE_APP_BUILD_MODE === 'package') return;

      if (this.$data.state.debug) return;

      const requestForDebug = this.$data.clickForDebuggingCount >= 6;
      this.$data.clickForDebuggingCount = 0;

      if (!requestForDebug) return;

      if (this.$data.debugUI) {
        this.$data.state.debug = true;
        return;
      }

      this.$data.debugUI = true;
    },

    onReady() {
      window.addEventListener('resize', this.onResize);
      this.$nextTick(() => { this.$data.state.renderReady = true; });
      this.onResize();
      this.play();
    },

    onCoinLoaded() {
      this.$el.addEventListener('mouseup', this.upHandler);
    },

    onIntroComplete() {
      this.play(this.$data.renderStopauto ? DEFAULT_PLAY_TIME : 0);
      this.addInteraction();
    },

    upHandler() {
      if (this.$data.state.debug) return;

      this.play(this.$data.renderStopauto ? DEFAULT_PLAY_TIME : 0);
      Rendering.flipCoin();
      Rendering.setCamOffset(0, 0);

      this.$data.clickForDebuggingCount += 1;
      clearTimeout(timeoutForDebug);
      timeoutForDebug = setTimeout(() => { this.clickForDebugging(); }, 500);
    },

    moveHandler(e) {
      if (this.$data.state.debug) return;

      this.play(DEFAULT_PLAY_TIME);
      Rendering.setCamOffset(
        ((e.clientX / window.innerWidth) - 0.5) * -3 * CAM_OFFSET_MOUSE_COEFF,
        ((e.clientY / window.innerHeight) - 0.5) * 2 * CAM_OFFSET_MOUSE_COEFF,
      );
    },

    gyroHandler({
      alpha,
      gamma,
      beta,
      acceleration,
    }) {
      if (this.$data.state.debug) return;

      // this.play(this.$data.state.debug ? null : DEFAULT_PLAY_TIME);

      if (acceleration) {
        this.$data.orientation = `GYRO ACC ${acceleration.x} ${acceleration.y} ${acceleration.z}`;
      } else if (alpha || beta || gamma) {
        /*
          alpha: rotation autour de l'axe z
          gamma: de gauche à droite
          bêta : mouvement avant-arrière
        */
        this.$data.orientation = `GYRO ABG ${alpha} ${beta} ${gamma}`;
        Rendering.setCamOffset(
          Math.max(Math.min(gamma / CAM_OFFSET_GYRO_HALF_ANGLE, 1), -1) * CAM_OFFSET_GYRO_COEFF,
          Math.max(Math.min((beta - 45) / CAM_OFFSET_GYRO_HALF_ANGLE, 1), -1) * -CAM_OFFSET_GYRO_COEFF,
        );
      }
    },

    play(timeout) {
      clearTimeout(timeoutPlay);
      this.$data.state.playing = true;
      // stop rendering on desktop (pointer-devices) after timeout
      if (!this.$data.state.touchDevice && timeout > 0 && this.$data.renderStopauto) {
        timeoutPlay = setTimeout(() => { this.$data.state.playing = false; }, timeout);
      }
    },

    addInteraction() {
      this.$data.state.interactionEnabled = true;
      window.addEventListener('mousemove', this.moveHandler);
    },

    onRendered() {
      this.$data.frameID += 1;
    },

    onResize() {
      setTimeout(() => {
        this.$data.state.touchDevice = isTouchDevice();
      }, 500);

      Rendering.updateSize();
      if (this.$data.state.playing) return;
      Rendering.render();
    },
  },
};
</script>

<style lang="stylus" scoped>
.viewer {
  // overflow: hidden;
  position: relative;

  width: 100%;
  height: 100%;

  background-color: #ffffff00;
  transition: background-color 0s;
  cursor: pointer;
  -webkit-tap-highlight-color: rgba(255, 255, 255, 0);

  .fade-enter-active,
  .fade-leave-active {
    transition: opacity 0.25s ease-in-out;
  }
}

.viewer--interaction-enabled:hover {
  &.viewer--theme-liberty {
    cursor: embedurl('../../assets/images/collections/liberty/common/flip-coin.svg', 'utf8') 20 20, pointer;
  }
  &.viewer--theme-equality {
    cursor: embedurl('../../assets/images/collections/equality/common/flip-coin.svg', 'utf8') 20 20, pointer;
  }
  &.viewer--theme-fraternity {
    cursor: embedurl('../../assets/images/collections/fraternity/common/flip-coin.svg', 'utf8') 20 20, pointer;
  }
}

.viewer__content {
  position: relative;
  z-index: 1;
  width: 100%;
  height: 100%;

  .viewer--interaction-enabled & {
    animation: drop-shadow-appear 1s ease forwards;
    @keyframes drop-shadow-appear {
      from {
        filter: drop-shadow(0 0 0 rgba(0,0,0, 0));
      }
      to {
        filter: drop-shadow(-50px 50px 25px rgba(0,0,0, 0.1));
      }
    }
  }
}

.viewer__render {
  box-sizing: border-box
  pointer-events: none;
  width:  100%;
  height: 100%;

  &--debug {
    pointer-events: auto;
  }
}

.viewer__mobile-ui {
  position: absolute;
  right: 0;
  bottom: 0;

  +mouseDevice() { display: none; }
}

.flip-coin {
  transition: transform 0.1s;
  .viewer:active & {
    transform: scale(0.9);
  }
}

.viewer__infos {
  position: absolute;
  top: 0;
  right: 0;

  font-size: 10px;
  font-family: Arial, Helvetica, sans-serif;
  color: white;
  background-color: #00000055;
}

.slot {
  pointer-events: auto;
}
</style>
